import { valueOf } from '../template/document'
import { field, type FormTemplate, type Run } from '../template/types'
import { el } from './dom'

/**
 * Renders the parsed blocks to the screen.
 *
 * The same block list feeds this and the PDF layout. One parsed document, two
 * renderers: a signer must not be shown one wording and have another written to
 * the file they sign.
 */
export function renderDocument(
  template: FormTemplate,
  values: Record<string, string>,
): HTMLElement {
  const container = el('article', { class: 'document', 'aria-label': template.title })

  for (const block of template.body) {
    switch (block.kind) {
      case 'heading': {
        const level = Math.min(block.level + 1, 6)
        const node = document.createElement(`h${level}`)
        node.className = `doc-h doc-h${block.level}`
        appendRuns(node, block.runs, values)
        container.append(node)
        break
      }
      case 'paragraph': {
        const node = el('p', { class: 'doc-p' })
        appendRuns(node, block.runs, values)
        container.append(node)
        break
      }
      case 'note': {
        const node = el('p', { class: 'doc-note' })
        appendRuns(node, block.runs, values)
        container.append(node)
        break
      }
      case 'bullet': {
        const node = el('p', { class: 'doc-bullet' }, [el('span', { class: 'doc-marker', text: block.marker })])
        appendRuns(node, block.runs, values)
        container.append(node)
        break
      }
      case 'rule':
        container.append(el('hr', { class: 'doc-rule' }))
        break
      case 'fieldLine':
        container.append(
          el('p', { class: 'doc-field' }, [
            el('span', { class: 'doc-field-label', text: `${field(template, block.key)?.label ?? block.key}: ` }),
            el('span', { class: 'doc-field-value', text: valueOf(values, block.key) }),
          ]),
        )
        break
      case 'signature': {
        const spec = template.signatures.find((s) => s.key === block.key)
        container.append(
          el('p', { class: 'doc-signature' }, [
            `${spec?.label ?? block.key}: `,
            el('em', { text: spec && !spec.required ? '(only if it applies)' : '(signed at the end)' }),
          ]),
        )
        break
      }
    }
  }

  return container
}

function appendRuns(node: HTMLElement, runs: Run[], values: Record<string, string>): void {
  for (const run of runs) {
    const text = run.placeholder ? valueOf(values, run.placeholder) : run.text
    if (run.placeholder) {
      node.append(el('strong', { class: 'doc-value', text }))
      continue
    }
    let child: Node = document.createTextNode(text)
    if (run.italic) {
      const em = document.createElement('em')
      em.append(child)
      child = em
    }
    if (run.bold) {
      const strong = document.createElement('strong')
      strong.append(child)
      child = strong
    }
    node.append(child)
  }
}
